/* eslint-disable @typescript-eslint/no-unused-vars */

import { useAlgoliaContext } from "providers/Algolia";
import { useInstantSearch } from "react-instantsearch-core";
import { toast } from "react-toastify";
import { EnrollmentOption } from "types/landlord/enrollment";
import { getErrorMessage } from "utils/error";
import { AlgoliaUser } from "views/renters/types";

export const prepareAlgoliaData = (
  data: AlgoliaUser[],
  enrollmentOption: EnrollmentOption
) => {
  return data.map((renter) => {
    const { units, manager, phone, email, ...property } = renter.property;
    const { boomCustomerId, pets, hasPets, ...customer } = renter.customer;
    const {
      _highlightResult,
      boomEnrollmentStatus,
      property: _property,
      customer: _customer,
      ...renterDetails
    } = renter;

    const ret = {
      ...renterDetails,
      property,
      customer: customer,
      opt_out: enrollmentOption.optOut,
      opt_in: enrollmentOption.optIn
    };

    return ret;
  });
};

export const useAlgoliaHelper = () => {
  const data = useAlgoliaContext();
  const index = data.payload?.index;

  // TODO: Maybe we should add filter by enrollment status here
  const { indexUiState } = useInstantSearch();

  const getAllFilteredRenters = async ({
    boomEnrollmentStatus
  }: {
    boomEnrollmentStatus?: "not_enrolled" | "error";
  }) => {
    const hitsPerPage = 100;

    const modifiedFacetFilters = indexUiState.refinementList
      ? Object.entries(indexUiState.refinementList).map(([key, values]) => {
          if (key === "boomEnrollmentStatus" && boomEnrollmentStatus)
            return values.map((value) => `${key}:${boomEnrollmentStatus}`);

          return values.map((value) => `${key}:${value}`);
        })
      : [];

    const [min, max] = indexUiState.range?.rent?.split(":") || [];

    const getNumericFilters = () => {
      if (min && max) return [[`rent <= ${max}`], [`rent >= ${min}`]];

      if (min) return [[`rent >= ${min}`]];

      if (max) return [[`rent <= ${max}`]];

      return [];
    };

    const numericFilters = getNumericFilters();

    try {
      if (!index) throw new Error("No index provided");

      const initialResult = await index.search(indexUiState.query || "", {
        facetFilters: modifiedFacetFilters,
        page: 0,
        numericFilters,
        hitsPerPage
      });

      let currentPage = initialResult.page + 1;
      const totalPages = initialResult.nbPages - 1;
      const promises: (Promise<unknown> | unknown)[] = [initialResult.hits];
      while (currentPage <= totalPages) {
        promises.push(
          index
            .search(indexUiState.query || "", {
              facetFilters: modifiedFacetFilters,
              numericFilters,
              page: currentPage,
              hitsPerPage
            })
            .then((res) => res.hits)
        );
        currentPage++;
      }
      const res = await Promise.all(promises);

      return res.flat() as AlgoliaUser[];
    } catch (error: unknown) {
      toast.error(
        getErrorMessage(
          error as
            | {
                message?: string;
              }
            | undefined
        )
      );
    }
  };

  return { getAllFilteredRenters };
};
